import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Month from '../Month/Month';
import {compareDateStrings} from '../../utils/dateUtils';
import dateUtils from '../utils/dateUtils';
import propTypeDateString from '../../utils/propTypeDateString';
import {
    safeWindowListenerAdd,
    safeWindowListenerRemove
} from '../utils/safeWindowListeners';
import LanguageTag from '../../utils/LanguageTag';
import classNames from 'classnames';
import debounce from 'lodash.debounce';

class Calendar extends Component {
    static propTypes = {
        /** The 0-11 index of the current month that has a date in focus. Used to update the focus to the correct month when updateFocus() is called.  */
        activeMonth: PropTypes.number,
        /** The 0-6 index of the last table cell column that received focus. Used to update the focus to the correct date when updateFocus() is called. */
        column: PropTypes.number,
        /** If you provide a dayTemplateComponent, you can also specify the data needed.  See the "Day Data" section above. */
        dayData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** If you need custom data displayed on each day, such as nightly rate, you can provide a template component and a set of data. Use this property for the component  */
        dayTemplateComponent: PropTypes.func,
        /** Last arrow key direction that was pressed while using keyboard navigation for a11y. ie: 'up', 'down', 'prev', 'next' */
        direction: PropTypes.string,
        /** Flag to specify whether year should be displayed first*/
        displayYearFirst: PropTypes.bool,
        /** Enable date range being the same date. By default, if the end date is selected to be the same as the start date, the entire selection is canceled */
        enableSameDayDateRange: PropTypes.bool,
        /** Object containing lingua translations for the current locale. It is assumed that the parent app will prepare and provide the i18n object matching the shape seen here. */
        i18n: PropTypes.shape({
            ariaLabelCheckinSelected: PropTypes.string,
            ariaLabelCheckoutSelected: PropTypes.string,
            ariaLabelNotValidCheckin: PropTypes.string,
            ariaLabelNotValidCheckout: PropTypes.string,
            ariaLabelUnavailable: PropTypes.string,
            ariaLabelCheckinCleared: PropTypes.string,
            ariaLabelCheckoutCleared: PropTypes.string,
            ariaLabelDatesCleared: PropTypes.string
        }),
        /** Optional labels object to override default locale-driven labels */
        labels: PropTypes.shape({
            dayNames: PropTypes.arrayOf(PropTypes.string),
            monthNames: PropTypes.arrayOf(PropTypes.string),
            shortDayNames: PropTypes.arrayOf(PropTypes.string),
            shortMonthNames: PropTypes.arrayOf(PropTypes.string),
            /** To support year labels for locales that need them e.g. ja-JP*/
            yearLabel: PropTypes.string
        }),
        /** A moniker or BCP-47 style language tag string. */
        locale: PropTypes.string,
        /** Zero-based index representing the month. The calendar stores nothing for month or year, it is up to the host app to provide those values. */
        month: PropTypes.number.isRequired,
        /** Horizontal alignment of label above each month */
        monthLabelAlignment: PropTypes.oneOf(['left', 'right', 'center']),
        /** The number of columns to break the months into. Used in conjunction with "numMonthsToDisplay" to achieve multiple use-cases */
        numColumns: PropTypes.number,
        /** The number of months to draw. Use this property in conjunction with the numColumns property to achieve your use-case */
        numMonthsToDisplay: PropTypes.number,
        /** onArrowNavigation(direction, column) Called when the beginning or end of the visible calendar range is navigated to via arrow keys. Used in conjunction with nav-calendar-month to navigate to prev/next month. */
        onArrowNavigation: PropTypes.func,
        /** onDisabledDayClick Called when clicked on a disabled date an alert with arrival and departure date triggers. */
        onDisabledDayClick: PropTypes.func,
        /** onMonthNameClicked(month, year) Called when the user clicks the month name label */
        onMonthNameClicked: PropTypes.func,
        /** onSelectionChanged(selectionStart, selectionEnd) Called when a date range selection changes. In some cases the selectionEnd will be null, such as when a selection is first initiated. */
        onSelectionChanged: PropTypes.func,
        /** Force all months to have 6 weeks rather than a varying number. This is automatically done in multi-column mode so that the rows align. */
        padWeeks: PropTypes.bool,
        /** Flag to determine if focus date needs to be updated. */
        refresh: PropTypes.bool,
        /** When using the calendar in a scrolling scenario, scroll to the selected month on render */
        scrollToMonth: PropTypes.bool,
        /** Turns on date range selection */
        selectable: PropTypes.bool,
        /** Format: "YYYY-MM-DD". For 'single' selectionType, this is the selected date */
        selectedDate: propTypeDateString,
        /** Once a selection is made, by default any clicking down will start a new selection. If you need to change the start or end date rather than replace the full selection, choose 'start' or 'end' here. We do this for the date range picker (for example) */
        selectionChangeMode: PropTypes.oneOf(['all', 'start', 'end']),
        /** Format: "YYYY-MM-DD". For date range selections, this is the end date. */
        selectionEnd: propTypeDateString,
        /** Format: "YYYY-MM-DD". For date range selections, this is the start date. */
        selectionStart: propTypeDateString,
        /** Type of selection 1) date range (default) 2) single date */
        selectionType: PropTypes.oneOf(['range', 'single']),
        /** Show/Hide gridlines around days */
        showGridlines: PropTypes.bool,
        /** Show/Hide the label above each month "September 2016" */
        showMonthLabels: PropTypes.bool,
        /** Show/Hide the days of the previous and next months to fill out start and end weeks. Showing them can be desireable for single months, but not with multiple month displays. */
        showSiblingMonths: PropTypes.bool,
        /** By default, the calender element is given a data-wdio attribute of "calendar".
        If you have more than one on a page you can add a prefix to those two strings by using this property. */
        wdioPrefix: PropTypes.string,
        /** e.g. 2016, number representing the year. The calendar stores nothing for month or year, it is up to the host app to provide those values. */
        year: PropTypes.number.isRequired
    };

    static defaultProps = {
        activeMonth: null,
        column: null,
        dayData: {},
        dayTemplateComponent: null,
        direction: null,
        displayYearFirst: false,
        enableSameDayDateRange: false,
        i18n: {
            ariaLabelCheckinCleared: 'Check-in date cleared.',
            ariaLabelCheckinSelected: '{0} is selected as your check-in date.',
            ariaLabelCheckoutCleared: 'Checkout date cleared.',
            ariaLabelCheckoutSelected: '{0} is selected as your checkout date.',
            ariaLabelDatesCleared: 'Dates cleared.',
            ariaLabelNotValidCheckin: '{0} is not a valid check in date.',
            ariaLabelNotValidCheckout: '{0} is not a valid checkout date.',
            ariaLabelUnavailable: '{0} is unavailable.'
        },
        locale: 'en',
        monthLabelAlignment: 'center',
        numColumns: 1,
        numMonthsToDisplay: 1,
        onArrowNavigation: () => {},
        onDisabledDayClick: () => {},
        onSelectionChanged: () => {},
        refresh: false,
        selectable: false,
        selectedDate: null, // Will be a date string
        selectionChangeMode: 'all',
        selectionEnd: null, // Will be a date string
        selectionStart: null, // Will be a date string
        selectionType: 'range',
        showGridlines: false,
        showMonthLabels: true,
        showSiblingMonths: false
    };

    constructor(props) {
        super(props);

        this.state = {
            ariaLiveLabel: '',
            hoverDate: null, // Will be a date string
            languageTag: new LanguageTag(this.props.locale)
        };

        this.handleDayHover = debounce(this.handleDayHover.bind(this), 35);

        this.initialMount = true;
        this.activeMonth = 0;
        this.monthRef = [];
    }

    componentDidMount() {
        this.sizeChanged();
        this.sizeChanged = debounce(this.sizeChanged.bind(this), 100);
        safeWindowListenerAdd('resize', this.sizeChanged);
        safeWindowListenerAdd('touchstart', this.onFirstTouch);
    }

    componentDidUpdate(prevProps) {
        if (this.props.selectionEnd !== prevProps.selectionEnd) {
            this.setState({hoverDate: this.props.selectionEnd});

            const selectionStartUpdated =
                this.props.selectionStart !== prevProps.selectionStart;
            const datesAreEmpty =
                this.props.selectionEnd === '' &&
                this.props.selectionStart === '';
            if (datesAreEmpty && selectionStartUpdated) {
                this.updateAriaLive(null, null); // Dates cleared, update aria-live label
            }
        }

        if (this.props.locale !== prevProps.locale) {
            this.setState({
                languageTag: new LanguageTag(this.props.locale)
            });
        }

        // The refresh prop is toggled every time the onArrowNavigation() callback function is used.
        // We use this as a way for nav-calendar-month to notify calendar-base that it is
        // ready to reset its focus after the new calendar months have been rendered.
        if (prevProps.refresh !== this.props.refresh) {
            this.updateFocus();
        }
    }

    componentWillUnmount() {
        this.sizeChanged.cancel();
        safeWindowListenerRemove('resize', this.sizeChanged);
        safeWindowListenerRemove('touchstart', this.onFirstTouch);
    }

    onFirstTouch = () => {
        // storing isTouch on the class as opposed to state since state was
        // causing a scrolling lag on iOS. This is fine for now since isTouch
        // is only used in this file, during a selection, not passed down the
        // component hierarchy.
        this.isTouch = true;
        safeWindowListenerRemove('touchstart', this.onFirstTouch);
    };

    sizeChanged() {
        const small =
            this.firstMonth && this.firstMonth.elem
                ? this.firstMonth.elem.clientWidth <= 480
                : false;
        if (small !== this.state.simple) {
            this.setState({simple: small});
        }
    }

    handleDayClick = (dateString) => {
        if (!dateString || !this.props.selectable) {
            return;
        }

        if (this.props.selectionType === 'range') {
            this.rangeSelection(dateString);
        } else {
            this.singleDateSelection(dateString);
        }
    };

    updateAriaLive = (dateString, position) => {
        const {i18n} = this.props;
        if (dateString !== null) {
            const event = dateUtils.getDateFromString(dateString);
            const options = {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC'
            };
            const code = this.state.languageTag.code;
            const date = event.toLocaleDateString(code, options);

            if (position === 'start') {
                this.setState({
                    ariaLiveLabel: i18n.ariaLabelCheckinSelected.replace(
                        '{0}',
                        date
                    )
                });
            } else if (position === 'end') {
                this.setState({
                    ariaLiveLabel: i18n.ariaLabelCheckoutSelected.replace(
                        '{0}',
                        date
                    )
                });
            }
        } else if (position === 'start') {
            this.setState({ariaLiveLabel: i18n.ariaLabelCheckinCleared});
        } else if (position === 'end') {
            this.setState((prevState) => ({
                ariaLiveLabel: `${i18n.ariaLabelCheckoutCleared} ${prevState.ariaLiveLabel}`
            }));
        } else {
            this.setState({ariaLiveLabel: i18n.ariaLabelDatesCleared});
        }
    };

    rangeSelection(dateString) {
        let {
            selectionStart,
            selectionEnd,
            selectionChangeMode,
            enableSameDayDateRange
        } = this.props;
        let {hoverDate} = this.state;

        // If there is already a start of selection, but no end
        if (
            (selectionStart && !selectionEnd) ||
            (selectionStart && selectionChangeMode === 'end')
        ) {
            const dateCompare = compareDateStrings(selectionStart, dateString);
            // Is the incoming date greater than the selectionStart or
            // is the same day selected and same day selection allowed?
            if (
                dateCompare > 0 ||
                (dateCompare === 0 && enableSameDayDateRange)
            ) {
                selectionEnd = dateString;
                this.updateAriaLive(selectionEnd, 'end');
            } else if (dateCompare === 0) {
                // if incoming date equals selectionStart cancel out the selection
                selectionStart = null;
                hoverDate = null;
                this.updateAriaLive(null, 'start');
            } else {
                selectionStart = dateString;
                selectionEnd = null;
                this.updateAriaLive(selectionStart, 'start');
            }
        } else {
            selectionStart = dateString;
            selectionEnd = null;
            hoverDate = null;
            this.updateAriaLive(selectionStart, 'start');
            if (this.props.selectionEnd) {
                this.updateAriaLive(null, 'end');
            }
        }

        if (!this.isTouch) {
            this.setState({hoverDate});
        }

        // Notify the outside world
        this.props.onSelectionChanged(selectionStart, selectionEnd);
    }

    singleDateSelection(dateString) {
        // Notify the outside world
        this.updateAriaLive(dateString, 'start');
        this.props.onSelectionChanged(dateString);
    }

    handleDayHover(dateString) {
        if (
            this.props.selectionStart &&
            !this.props.selectionEnd &&
            this.props.selectable
        ) {
            this.setState({hoverDate: dateString});
        }
    }

    // Used to reset focus on the correct date after nav-calendar-month updates the months that are displayed
    updateFocus = () => {
        const {direction, column, activeMonth} = this.props;
        this.setActiveMonth(activeMonth);

        if (direction === 'next' || direction === 'prev') {
            this.setMonthFocus(direction);
        } else if (direction === 'up' || direction === 'down') {
            this.setMonthFocus(direction, column);
        }
    };

    setActiveMonth = (i) => {
        this.activeMonth = i;
    };

    // Used to reset the month and day focus when arrow nagivation moves outside the current month.
    // eslint-disable-next-line complexity
    setMonthFocus = (direction, i) => {
        const {onArrowNavigation} = this.props;
        const currentIndex = this.activeMonth;
        let numOfRows;

        // Increment the active month index and set the focus on the first day of the next month.
        if (direction === 'next') {
            if (currentIndex < this.monthRef.length - 1) {
                this.activeMonth = currentIndex + 1;
                numOfRows =
                    this.monthRef[this.activeMonth].reference.length - 1;
                this.handleRightArrow(0, -1, numOfRows);
            } else {
                // If we've reached the last visible month, notify the parent component so we can continue navigation.
                onArrowNavigation(direction);
            }
            // Decrement the active month index and set the focus on the last day of the previous month.
        } else if (direction === 'prev') {
            if (currentIndex > 0) {
                this.activeMonth = currentIndex - 1;
                numOfRows =
                    this.monthRef[this.activeMonth].reference.length - 1;
                this.handleLeftArrow(numOfRows, 7);
            } else {
                // If we've reached the first visible month, notify the parent component.
                onArrowNavigation(direction);
            }
            // Increment the active month index and set the focus on the same day of the first week in the next month.
        } else if (direction === 'down') {
            if (currentIndex < this.monthRef.length - 1) {
                this.activeMonth = currentIndex + 1;
                numOfRows =
                    this.monthRef[this.activeMonth].reference.length - 1;
                this.handleDownArrow(-1, i, numOfRows);
            } else {
                // If we've reached the last visible month, notify the parent component.
                onArrowNavigation(direction, i);
            }
            // Decrement the active month index and set the focus on the same day of the last week of the previous month.
        } else if (direction === 'up') {
            if (currentIndex > 0) {
                this.activeMonth = currentIndex - 1;
                numOfRows = this.monthRef[this.activeMonth].reference.length;
                this.handleUpArrow(numOfRows, i);
            } else {
                // If we've reached the first visible month, notify the parent component.
                onArrowNavigation(direction, i);
            }
        }
    };

    handleRightArrow = (row, col, numOfRows) => {
        const i = this.activeMonth;
        // If focus is on the last day of a week and the right arrow key is pressed,
        // move focus to the first day of the next week
        if (col === 6 && row < numOfRows) {
            row++;
            col = 0;
            // If focus is on the last day of the month and the right arrow key is pressed,
            // move the focus to first day of the next month
        } else if (col === 6 && row === numOfRows) {
            this.setMonthFocus('next');
            return;
            // If neither of the above are true, then just move focus to the next day
        } else {
            col++;
        }

        if (this.monthRef[i].reference[row][col].tabIndex !== -2) {
            this.monthRef[i].reference[row][col].focus();
        } else {
            // Couldn't set the focus on the new day, so try again with the next cell to the right.
            this.handleRightArrow(row, col, numOfRows);
        }
    };

    handleLeftArrow = (row, col) => {
        const i = this.activeMonth;
        // If focus is on the first day of the month and the left arrow key is pressed,
        // move the focus to last day of the previous month
        if (col === 0 && row === 0) {
            this.setMonthFocus('prev');
            return;
            // If focus is on the first day of a week and the left arrow key is pressed,
            // move focus to the last day of the previous week
        } else if (col === 0 && row !== 0) {
            row--;
            col = 6;
            // If neither of the above are true, then just move focus to the previous day
        } else if (col !== 0) {
            col--;
        }

        if (this.monthRef[i].reference[row][col].tabIndex !== -2) {
            this.monthRef[i].reference[row][col].focus();
        } else {
            // Couldn't set the focus on the new day, so try again with the next cell to the left.
            this.handleLeftArrow(row, col);
        }
    };

    handleDownArrow = (row, col, numOfRows) => {
        const i = this.activeMonth;
        // If we aren't on the last week of the month,
        // then move focus to same day of the next week.
        if (row < numOfRows) {
            row++;
            if (this.monthRef[i].reference[row][col].tabIndex !== -2) {
                this.monthRef[i].reference[row][col].focus();
            } else {
                this.handleDownArrow(row, col, numOfRows); // Couldn't set the focus on the new day, so try again with the next cell below.
            }
            // If the down arrow key is pressed while on the last week of a month,
            // move focus to the next month as long as we aren't already in the last month of the array
        } else if (i <= this.monthRef.length - 1) {
            this.setMonthFocus('down', col);
        }
    };

    handleUpArrow = (row, col) => {
        const i = this.activeMonth;
        // If we aren't on the first week of the month,
        // then move focus to same day of the previous week.
        if (row !== 0) {
            row--;
            if (this.monthRef[i].reference[row][col].tabIndex !== -2) {
                this.monthRef[i].reference[row][col].focus();
            } else {
                this.handleUpArrow(row, col); // Couldn't set the focus on the new day, so try again with the next cell above.
            }
            // If the up arrow key is pressed while on the first week of a month,
            // move focus to the previous month as long as we aren't already in the first month of the array
        } else if (i >= 0) {
            this.setMonthFocus('up', col);
        }
    };

    renderMonths() {
        let months = [];
        this.monthRef = [];
        const {numColumns} = this.props;

        // We determine the "small" styling by rendering an empty div and evaluating its width.
        // Then a second render is triggered that renders all requested months.
        if (this.initialMount) {
            this.initialMount = false;
            return (
                <div
                    style={{width: `${100 / numColumns}%`}}
                    ref={
                        /* istanbul ignore next */
                        (c) => (this.firstMonth = {elem: c})
                    }
                />
            );
        }

        const numMonths = this.props.numMonthsToDisplay;

        for (let i = 0; i <= numMonths - 1; i++) {
            const {month, year} = dateUtils.addMonthsToMonth(
                this.props.year,
                this.props.month,
                i
            );

            months.push(
                <Month
                    dayData={this.props.dayData}
                    dayTemplateComponent={this.props.dayTemplateComponent}
                    displayYearFirst={this.props.displayYearFirst}
                    handleDownArrow={this.handleDownArrow}
                    handleLeftArrow={this.handleLeftArrow}
                    handleRightArrow={this.handleRightArrow}
                    handleUpArrow={this.handleUpArrow}
                    hoverDate={this.state.hoverDate}
                    index={i}
                    i18n={this.props.i18n}
                    key={`month-${year}-${month}`}
                    labels={this.props.labels}
                    languageTag={this.state.languageTag}
                    locale={this.props.locale}
                    month={month}
                    monthLabelAlignment={this.props.monthLabelAlignment}
                    numMonthsToDisplay={this.props.numMonthsToDisplay}
                    onDayClick={this.handleDayClick}
                    onDayHover={this.handleDayHover}
                    onDisabledDayClick={this.props.onDisabledDayClick}
                    onMonthNameClicked={this.props.onMonthNameClicked}
                    padWeeks={this.props.padWeeks}
                    scrollToMonth={this.props.scrollToMonth}
                    selectable={this.props.selectable}
                    selectedDate={this.props.selectedDate}
                    selectionEnd={this.props.selectionEnd}
                    selectionStart={this.props.selectionStart}
                    selectionType={this.props.selectionType}
                    setActiveMonth={this.setActiveMonth}
                    simple={this.state.simple}
                    showGridlines={this.props.showGridlines}
                    showMonthLabels={this.props.showMonthLabels}
                    showSiblingMonths={this.props.showSiblingMonths}
                    ref={(ref) => {
                        /* istanbul ignore next */
                        if (i === 0) {
                            this.firstMonth = ref;
                        }
                        this.monthRef[i] = ref;
                    }}
                    width={`${100 / numColumns}%`}
                    year={year}
                />
            );
        }

        return months;
    }

    render() {
        const {className, wdioPrefix} = this.props;
        const calendarClasses = classNames(className, 'calendar', 'clearfix');
        const dataWdio = wdioPrefix ? `${wdioPrefix}-calendar` : 'calendar';

        return (
            <div className={calendarClasses} data-wdio={dataWdio}>
                <span className="sr-only" aria-live={'polite'}>
                    {this.state.ariaLiveLabel}
                </span>
                {this.renderMonths()}
            </div>
        );
    }
}

export default Calendar;
